import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback } from "react";

import { CancellationProtectionBanner } from "./CancellationProtectionBanner";
import { CancellationProtectionTermsDialog } from "./CancellationProtectionTermsDialog";
import { useCancellationProtectionConfig } from "./hooks/useCancellationProtectionConfig";

const CANCELLATION_PROTECTION_BANNER_STORAGE_KEY_PREFIX =
  "cancellation-protection-banner-dismissed";

function getBannerStorageKey(workerId: string): string {
  return `${CANCELLATION_PROTECTION_BANNER_STORAGE_KEY_PREFIX}-${workerId}`;
}

export function CancellationProtectionBannerEntrypoint() {
  const { userId: workerId } = useDefinedWorker();
  const cancellationProtectionConfig = useCancellationProtectionConfig();
  const isBannerEnabled =
    cancellationProtectionConfig.enabled && cancellationProtectionConfig.isBannerEnabled;
  const termsDialogState = useModalState();

  const storageKey = getBannerStorageKey(workerId);
  const [isDismissed, setIsDismissed] = useLocalStorage<boolean>(storageKey, false);

  const shouldShowBanner = isBannerEnabled && !isDismissed;

  useLogEffect(
    APP_V2_USER_EVENTS.CANCELLATION_PROTECTION_BANNER_VIEWED,
    { workerId },
    { enabled: shouldShowBanner }
  );

  const handleViewTerms = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.CANCELLATION_PROTECTION_BANNER_VIEW_TERMS_CLICKED, {
      workerId,
    });
    termsDialogState.openModal();
  }, [workerId, termsDialogState]);

  const handleDismiss = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.CANCELLATION_PROTECTION_BANNER_DISMISSED, {
      workerId,
    });
    setIsDismissed(true);
  }, [workerId, setIsDismissed]);

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <>
      <CancellationProtectionBanner onViewTerms={handleViewTerms} onDismiss={handleDismiss} />
      <CancellationProtectionTermsDialog modalState={termsDialogState} />
    </>
  );
}
